import { ComponentType, useCallback, useContext, useMemo } from "react";

import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { Identifiable } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { AutocompleteProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/input/autocomplete";

import { RESOURCE_KEYS } from "../localization";
import { assertTrue } from "../utilities/assert";

import { CustomAutocomplete } from "./CustomAutocomplete";
import { LabelDecorator } from "./LabelDecorator";

const COMPONENT_RESOURCE_KEYS = RESOURCE_KEYS.component.input.clientSelection;

export interface ClientSelectionInputProps extends Identifiable {
	readonly client?: string;
	readonly clients: string[];
	onChange(client: string): void;
}

const REQUIRED_INPUT_PROPS: React.HTMLProps<HTMLInputElement> = { "aria-required": true };

export const ClientSelectionInput: ComponentType<ClientSelectionInputProps> = function ClientSelectionInput({
	id,
	client,
	clients,
	onChange
}) {
	const { localizer } = useContext(LocalizerContext);

	const items = useMemo(() => clients.filter(it => !!it), [clients]);

	const onValueChange = useCallback<NonNullable<AutocompleteProps["onValueChange"]>>(
		client => {
			assertTrue(typeof client === "string");
			onChange(client);
		},
		[onChange]
	);

	const required = clients.length === items.length;
	return (
		<CustomAutocomplete
			id={id}
			label={
				<LabelDecorator required={required}>{localizer({ key: COMPONENT_RESOURCE_KEYS.label })}</LabelDecorator>
			}
			inputProps={required ? REQUIRED_INPUT_PROPS : undefined}
			initialValue={client}
			items={items}
			onValueChange={onValueChange}
			hintTemplate={
				localizer({
					key: RESOURCE_KEYS.component.autocomplete.hintTemplate,
					args: {
						count: { type: "plain", value: "{count}" },
						total: { type: "plain", value: "{total}" }
					}
				}) ?? "[{count} / {total}]"
			}
		/>
	);
};
